import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PricingTier, PricingTierMeta } from '@/types/plans';

interface PlanFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: PricingTier | null;
  meta: PricingTierMeta;
  onSaved: () => void;
}

/** Empty string means "not set", which is sent as null. */
function toNullableNumber(value: string): number | null {
  return value.trim() === '' ? null : Number(value);
}

/** Empty string means "not set"; a missing seat bound comes back as null. */
function toSeatField(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * The form lives in its own component so that the `key` on it, rather than an
 * effect, is what resets the fields. DialogContent unmounts its children when
 * closed, so a fresh mount per tier gives every field its value from props at
 * first render instead of painting the previously edited tier for a frame and
 * correcting it afterwards.
 */
export function PlanFormDialog({ open, onOpenChange, tier, meta, onSaved }: PlanFormDialogProps) {
  const isEdit = tier !== null;
  const pricingDisabled = isEdit && (tier.lockedFields ?? []).includes('price');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${tier.name}` : 'Add plan'}</DialogTitle>
          <DialogDescription>
            {pricingDisabled
              ? 'Pricing is locked because groups are subscribed to this plan. To change it, add a new plan and retire this one.'
              : 'Plans are what groups subscribe to. Pricing locks as soon as the first group subscribes.'}
          </DialogDescription>
        </DialogHeader>

        <PlanForm key={tier?.uuid ?? 'new'} tier={tier} meta={meta} onOpenChange={onOpenChange} onSaved={onSaved} />
      </DialogContent>
    </Dialog>
  );
}

type PlanField =
  | 'name'
  | 'pricingModel'
  | 'billingCycle'
  | 'price'
  | 'pricePerUser'
  | 'currency'
  | 'trialDays'
  | 'minUsers'
  | 'maxUsers'
  | 'features';
type PlanErrors = Partial<Record<PlanField, string>>;

/** Mirrors PricingTierController::rules(). */
const NAME_MAX = 255;
const FEATURE_MAX = 255;
const INTEGER = /^\d+$/;
const CURRENCY_CODE = /^[A-Za-z]{3}$/;

function isNonNegativeNumber(value: string): boolean {
  return value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;
}

function PlanForm({ tier, meta, onOpenChange, onSaved }: Omit<PlanFormDialogProps, 'open'>) {
  const isEdit = tier !== null;
  const locked = tier?.lockedFields ?? [];
  // The API locks the whole pricing group together, so one flag covers the
  // disabled state, what gets validated, and what gets sent.
  const pricingLocked = isEdit && locked.includes('pricing_model');

  const [name, setName] = useState(tier?.name ?? '');
  // Blank on create so the model and cycle are deliberate choices. An edit
  // keeps the tier's saved values.
  const [pricingModel, setPricingModel] = useState(tier?.pricingModel ?? '');
  const [price, setPrice] = useState(String(tier?.price ?? 0));
  const [pricePerUser, setPricePerUser] = useState(tier?.pricePerUser ?? '');
  const [currency, setCurrency] = useState(tier?.currency ?? 'USD');
  const [billingCycle, setBillingCycle] = useState(tier?.billingCycle ?? '');
  const [maxUsers, setMaxUsers] = useState(toSeatField(tier?.maxUsers));
  const [minUsers, setMinUsers] = useState(toSeatField(tier?.minUsers));
  const [trialDays, setTrialDays] = useState(String(tier?.trialDays ?? 0));
  const [isActive, setIsActive] = useState(tier?.isActive ?? true);
  // One feature per line: a repeating-row editor buys nothing for a list that
  // is edited once a quarter.
  const [features, setFeatures] = useState((tier?.features ?? []).join('\n'));
  const [errors, setErrors] = useState<PlanErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pricePerUserRequired = !pricingLocked && pricingModel === 'per_user';

  function clearError(field: PlanField) {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function validate(featureList: string[]): PlanErrors {
    const next: PlanErrors = {};

    if (!name.trim()) next.name = 'Name is required.';
    else if (name.length > NAME_MAX) next.name = `Name must be at most ${NAME_MAX} characters.`;

    // Locked pricing fields are disabled and never sent, so nothing about them
    // may block saving the fields that are still editable.
    if (!pricingLocked) {
      if (!pricingModel) next.pricingModel = 'Pricing model is required.';
      if (!billingCycle) next.billingCycle = 'Billing cycle is required.';
      if (!currency.trim()) next.currency = 'Currency is required.';
      else if (!CURRENCY_CODE.test(currency)) next.currency = 'Currency must be a 3-letter code.';
      if (price.trim() !== '' && !INTEGER.test(price.trim())) {
        next.price = 'Price must be a whole number of 0 or more.';
      }
      if (pricePerUser.trim() === '') {
        if (pricePerUserRequired) next.pricePerUser = 'Price per user is required.';
      } else if (!isNonNegativeNumber(pricePerUser)) {
        next.pricePerUser = 'Price per user must be 0 or more.';
      }
    }

    if (trialDays.trim() !== '' && !INTEGER.test(trialDays.trim())) {
      next.trialDays = 'Trial days must be a whole number of 0 or more.';
    }

    const seatError = (value: string, label: string) =>
      value.trim() !== '' && (!INTEGER.test(value.trim()) || Number(value) < 1)
        ? `${label} must be a whole number of 1 or more.`
        : undefined;
    next.minUsers = seatError(minUsers, 'Minimum members');
    next.maxUsers = seatError(maxUsers, 'Maximum members');
    if (!next.minUsers && !next.maxUsers && minUsers.trim() !== '' && maxUsers.trim() !== '') {
      if (Number(minUsers) > Number(maxUsers)) {
        next.minUsers = 'Minimum members must not be greater than maximum members.';
      }
    }

    if (featureList.some((feature) => feature.length > FEATURE_MAX)) {
      next.features = `Each feature must be at most ${FEATURE_MAX} characters.`;
    }

    return Object.fromEntries(Object.entries(next).filter(([, message]) => message)) as PlanErrors;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const featureList = features
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const nextErrors = validate(featureList);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);

    // On edit, locked fields are omitted entirely rather than sent unchanged.
    // The API refuses a locked field by its PRESENCE in the payload, so echoing
    // back an identical price would still be a 422.
    const payload: Record<string, unknown> = {
      name,
      max_users: toNullableNumber(maxUsers),
      min_users: toNullableNumber(minUsers),
      trial_days: Number(trialDays),
      is_active: isActive,
      features: featureList,
    };

    if (!pricingLocked) {
      payload.pricing_model = pricingModel;
      payload.price = Number(price);
      payload.price_per_user = pricePerUser.trim() === '' ? null : Number(pricePerUser);
      payload.currency = currency;
      payload.billing_cycle = billingCycle;
    }

    try {
      if (isEdit) {
        await api.patch(`/pricing-tiers/${tier.uuid}`, payload);
      } else {
        await api.post('/pricing-tiers', payload);
      }
      toast.success(isEdit ? `${name} updated.` : `${name} created.`);
      onSaved();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit} noValidate>
      <div className="space-y-2">
        <Label htmlFor="plan-name" required>Name</Label>
        <Input
          id="plan-name"
          value={name}
          maxLength={NAME_MAX}
          aria-invalid={!!errors.name}
          onChange={(e) => {
            setName(e.target.value);
            clearError('name');
          }}
          required
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="plan-model" required={!pricingLocked}>Pricing model</Label>
          <Select
            value={pricingModel}
            onValueChange={(value) => {
              setPricingModel(value);
              clearError('pricingModel');
              // Switching away from per_user drops the "required" on the
              // per-user price, so its error must not linger either.
              clearError('pricePerUser');
            }}
            disabled={pricingLocked}
          >
            <SelectTrigger id="plan-model" aria-label="Pricing model" aria-invalid={!!errors.pricingModel}>
              <SelectValue placeholder="-- Select pricing model --" />
            </SelectTrigger>
            <SelectContent>
              {meta.pricingModels.map((model) => (
                <SelectItem key={model} value={model} className="capitalize">
                  {model.replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.pricingModel && <p className="text-xs text-destructive">{errors.pricingModel}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-cycle" required={!pricingLocked}>Billing cycle</Label>
          <Select
            value={billingCycle}
            onValueChange={(value) => {
              setBillingCycle(value);
              clearError('billingCycle');
            }}
            disabled={pricingLocked}
          >
            <SelectTrigger id="plan-cycle" aria-label="Billing cycle" aria-invalid={!!errors.billingCycle}>
              <SelectValue placeholder="-- Select billing cycle --" />
            </SelectTrigger>
            <SelectContent>
              {meta.billingCycles.map((cycle) => (
                <SelectItem key={cycle} value={cycle} className="capitalize">
                  {cycle}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.billingCycle && <p className="text-xs text-destructive">{errors.billingCycle}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-price">Price</Label>
          <Input
            id="plan-price"
            type="number"
            min="0"
            step="1"
            value={price}
            aria-invalid={!!errors.price}
            onChange={(e) => {
              setPrice(e.target.value);
              clearError('price');
            }}
            disabled={pricingLocked}
          />
          {errors.price && <p className="text-xs text-destructive">{errors.price}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-price-per-user" required={pricePerUserRequired}>Price per user</Label>
          <Input
            id="plan-price-per-user"
            type="number"
            min="0"
            step="0.01"
            value={pricePerUser}
            aria-invalid={!!errors.pricePerUser}
            onChange={(e) => {
              setPricePerUser(e.target.value);
              clearError('pricePerUser');
            }}
            disabled={pricingLocked}
          />
          {errors.pricePerUser && <p className="text-xs text-destructive">{errors.pricePerUser}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-currency" required={!pricingLocked}>Currency</Label>
          <Input
            id="plan-currency"
            value={currency}
            maxLength={3}
            aria-invalid={!!errors.currency}
            onChange={(e) => {
              setCurrency(e.target.value.toUpperCase());
              clearError('currency');
            }}
            disabled={pricingLocked}
          />
          {errors.currency && <p className="text-xs text-destructive">{errors.currency}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-trial">Trial days</Label>
          <Input
            id="plan-trial"
            type="number"
            min="0"
            step="1"
            value={trialDays}
            aria-invalid={!!errors.trialDays}
            onChange={(e) => {
              setTrialDays(e.target.value);
              clearError('trialDays');
            }}
          />
          {errors.trialDays && <p className="text-xs text-destructive">{errors.trialDays}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-min">Minimum members</Label>
          <Input
            id="plan-min"
            type="number"
            min="1"
            step="1"
            value={minUsers}
            aria-invalid={!!errors.minUsers}
            onChange={(e) => {
              setMinUsers(e.target.value);
              clearError('minUsers');
            }}
          />
          {errors.minUsers && <p className="text-xs text-destructive">{errors.minUsers}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-max">Maximum members</Label>
          <Input
            id="plan-max"
            type="number"
            min="1"
            step="1"
            value={maxUsers}
            aria-invalid={!!errors.maxUsers}
            onChange={(e) => {
              setMaxUsers(e.target.value);
              clearError('maxUsers');
              // The min <= max check is reported on the minimum.
              clearError('minUsers');
            }}
          />
          {errors.maxUsers && <p className="text-xs text-destructive">{errors.maxUsers}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="plan-features">Features, one per line</Label>
        <textarea
          id="plan-features"
          className="flex min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive"
          value={features}
          aria-invalid={!!errors.features}
          onChange={(e) => {
            setFeatures(e.target.value);
            clearError('features');
          }}
        />
        {errors.features && <p className="text-xs text-destructive">{errors.features}</p>}
      </div>

      <div className="flex items-center gap-3">
        <Switch id="plan-active" checked={isActive} onCheckedChange={setIsActive} />
        <Label htmlFor="plan-active">Offered to new subscribers</Label>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : isEdit ? 'Save changes' : 'Create plan'}
        </Button>
      </DialogFooter>
    </form>
  );
}
